import React, { useEffect, useRef, useState } from "react";
import { api, downloadPrivateDocument, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, FileText, ExternalLink, Sparkles } from "lucide-react";
import { applyVisura } from "@/lib/applyVisura";

const LABELS = { company_name: "Ragione sociale", vat_number: "Partita IVA", fiscal_code: "Codice fiscale", address: "Indirizzo", zip: "CAP", city: "Città", province: "Provincia", pec: "PEC" };

const DOC_TYPES = [
  { key: "visura", label: "Visura Camerale" },
  { key: "id_document", label: "Documento d'identità" },
  { key: "contract", label: "Contratto" },
  { key: "other", label: "Altro" },
];

export default function Documents() {
  const { user, refresh } = useAuth();
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState("visura");
  const [uploading, setUploading] = useState(false);
  const [reading, setReading] = useState(false), [differ, setDiffer] = useState(null);
  const fileRef = useRef();

  const syncVisura = async (blob, name) => {
    setReading(true);
    try {
      const r = await applyVisura(blob, name);
      await refresh();
      toast.success(r.filled.length ? `Anagrafica aggiornata: ${r.filled.map((k) => LABELS[k]).join(", ")}` : "Visura letta: nessun dato mancante da aggiungere");
      setDiffer(Object.keys(r.differ).length ? r.differ : null);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Lettura della visura non riuscita: puoi inserire i dati a mano"); }
    finally { setReading(false); }
  };
  const fromSaved = async (d) => {
    const { data } = await api.get(`/files/${d.storage_path.split("/").map(encodeURIComponent).join("/")}`, { responseType: "blob" });
    await syncVisura(data, d.original_filename || "visura.pdf");
  };
  const applyDiffer = async () => {
    try { await api.put("/account/profile", differ); await refresh(); toast.success("Dati sostituiti con quelli della visura"); setDiffer(null); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Aggiornamento non riuscito"); }
  };

  const load = () => api.get("/auth/me").then((r) => setDocs(r.data.documents || [])).catch(() => {});
  useEffect(() => { setDocs(user?.documents || []); load(); /* eslint-disable-next-line */ }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    try {
      await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato");
      await refresh();
      load();
      if (docType === "visura") syncVisura(file, file.name);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore caricamento");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const typeLabel = (k) => DOC_TYPES.find((d) => d.key === k)?.label || k;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="documents-page">
      <div>
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Onboarding dealer</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1">Documenti & Visura</h1>
      </div>

      <Card className="p-6 glow-border">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-4">
          <div className="flex-1">
            <label className="text-sm font-medium">Tipo di documento</label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger className="mt-1.5" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{DOC_TYPES.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={onUpload} data-testid="dealer-register-visura-upload" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-2" data-testid="upload-trigger-button">
            <Upload className="w-4 h-4" /> {uploading ? "Caricamento…" : "Carica documento"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Formati accettati: PDF, PNG, JPG. Dalla visura aggiorniamo in automatico i dati mancanti dell'anagrafica; poi la verifica l'amministratore.</p>
        {reading && <p className="text-sm text-primary mt-2 flex items-center gap-2" data-testid="visura-reading"><Sparkles className="w-4 h-4 animate-pulse" /> Lettura della visura in corso…</p>}
        {differ && <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-2" data-testid="visura-differ">
          <p className="text-sm font-medium">La visura riporta dati diversi da quelli salvati:</p>
          <table className="text-sm"><tbody>{Object.entries(differ).map(([k, v]) => <tr key={k}><td className="pr-3 text-muted-foreground">{LABELS[k]}</td><td className="font-medium">{v}</td></tr>)}</tbody></table>
          <div className="flex gap-2"><Button size="sm" onClick={applyDiffer} data-testid="visura-differ-confirm">Usa i dati della visura</Button><Button size="sm" variant="ghost" onClick={() => setDiffer(null)} data-testid="visura-differ-ignore">Mantieni i miei</Button></div>
        </div>}
      </Card>

      <Card className="p-6 glow-border">
        <h3 className="font-head font-semibold mb-4">Documenti caricati</h3>
        <div className="space-y-3">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/40" data-testid={`doc-item-${d.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0"><FileText className="w-4 h-4" /></div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{d.original_filename}</p>
                  <Badge variant="outline" className="text-xs mt-0.5">{typeLabel(d.doc_type)}</Badge>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {d.doc_type === "visura" && <Button size="sm" variant="outline" className="gap-1.5" disabled={reading} onClick={() => fromSaved(d).catch(() => { setReading(false); toast.error("Documento non disponibile. Riprova."); })} data-testid={`doc-sync-${d.id}`}><Sparkles className="w-3.5 h-3.5" /> Aggiorna anagrafica</Button>}
                <button onClick={() => downloadPrivateDocument(d).catch(() => toast.error("Documento non disponibile. Riprova."))} aria-label={`Scarica ${d.original_filename}`} className="text-primary shrink-0" data-testid={`doc-view-${d.id}`}>
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {docs.length === 0 && <p className="text-muted-foreground text-sm">Nessun documento caricato.</p>}
        </div>
      </Card>
    </div>
  );
}
